import React, {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";

import {AlertProvider, useAlert} from "../Components/AlertContext";
import {ConfirmationProvider} from "../Components/ConfirmationContext";
import {TaxonomyDictionaryApiProvider, useTaxonomyDictionaryApi} from "../Services/TaxonomyDictionaryApiProvider";
import {DictionaryTermApiProvider, useDictionaryTermApi} from "../Services/DictionaryTermApiProvider";

import Breadcrumbs from "../Components/Breadcrumbs";
import Button from "../Components/Buttons";
import {CustomInput, CustomSelect, CustomTextarea, CustomToggle} from "../Components/CustomFormElements";
import {isEmpty} from "../utils/validationUtil";
import PageLoader from "../Components/PageLoader";

import "../Components/TaxonomyDictionary.css";
import {ThematicApiProvider, useThematicApi} from "../Services/ThematicApiProvider";
import {ChannelApiProvider, useChannelApi} from "../Services/ChannelApiProvider";

function ThematicContent({canEdit}) {
  const {organizationId, thematicId} = useParams();
  const navigate = useNavigate();
  const {showAlert} = useAlert();

  const {
    getThematicById,
    createThematic,
    updateThematic,
  } = useThematicApi();
  const { getChannels } = useChannelApi();

  const [loading, setLoading] = useState(false);
  const [isNew, setIsNew] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);

  const [channelsList, setChannelsList] = useState([]);
  const [selectedChannelIds, setSelectedChannelIds] = useState([]);

  useEffect(() => {
    const fetchThematic = async () => {
      setLoading(true);
      try {

        // Fetch channels
        const channelsResponse = await getChannels(0, 500);
        setChannelsList(channelsResponse.items || []);

        if (thematicId !== "new-thematic") {
          const response = await getThematicById(thematicId);
          setTitle(response.name);
          setDescription(response.description || "");
          setIsActive(response.is_active);
          setSelectedChannelIds(response.channel_keys || []);
          setIsNew(false);
          setIsDisabled(!canEdit);

        } else {
          setIsNew(true);
          setIsDisabled(!canEdit);
        }
      } catch {
        showAlert("fail", "Failed to load thematic");
      } finally {
        setLoading(false);
      }
    };

    fetchThematic();
  }, [thematicId, canEdit]);

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/thematic`);
  };

  const onSave = async () => {
    if (isEmpty(title)) {
      showAlert("fail", "Please enter a name");
      return;
    }

    const data = {name: title, description: description, channel_ids: selectedChannelIds, is_active: isActive};

    setLoading(true);
    try {
      if (isNew) {
        await createThematic(data);
        showAlert("success", "Thematic created!");
        navigate(`/organizations/${organizationId}/thematic`);
      } else {
        await updateThematic(thematicId, data);
        showAlert("success", "Thematic updated!");
      }
    } catch {
      showAlert("fail", "Failed to save Thematic");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <PageLoader/>;

  return (
    <div className="container">
      <Breadcrumbs/>
      <div className="row">
        <div className="col-6 radius-yes p-2 bg-yes">
          <h2 className="h2-title">
            {isNew ? "Create Thematic" : `Edit Thematic: ${title}`}
          </h2>

          <div className='input-block d-flex flex-row justify-between'>
            <div className='input-description'>
              <div className='input-title'>Thematic name</div>
              <div className='small-font'>Enter thematic name</div>
            </div>
            <div className='align-wrapper d-flex align-center'>
              <CustomInput
                id="thematic-name"
                label="Thematic Name"
                placeholder="Enter thematic name"
                type="text"
                icon={<i className="fa-solid fa-comments"></i>}
                value={title}
                onChange={(id, value) => setTitle(value)}
                disabled={isDisabled}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Thematic Description</div>
              <div className="small-font">Enter thematic description</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomTextarea
                id="description"
                label="Description"
                name="description"
                type="text"
                addedText={description}
                onChange={(value) => setDescription(value)}
                resizable={false}
                disabled={isDisabled}
                height="200px"
              />
            </div>
          </div>

          <div className='input-block d-flex flex-row justify-between'>
            <div className='input-description'>
              <div className='input-title'>Channels</div>
              <div className='small-font'>Select channels</div>
            </div>
            <div className='align-wrapper d-flex align-center'>
              <CustomSelect
                  id="channels-select"
                  label="Channels"
                  options={channelsList.map((c) => ({
                    value: c.id,
                    label: c.name,
                  }))}
                  value={selectedChannelIds}
                  multiSelect={true}
                  onChange={(val) => setSelectedChannelIds(val)}
                  disabled={isDisabled}
              />
            </div>
          </div>

          <div className='input-row input-block d-flex flex-row justify-between'>
            <div className='input-description'>
              <div className='input-title'>Activate thematic</div>
              <div className='small-font'>Set thematic to be active or inactive to enable or disable it</div>
            </div>
            <div className='align-wrapper d-flex align-center'>
              <CustomToggle
                id="toggle-status"
                label={isActive ? 'Active' : 'Inactive'}
                checked={isActive}
                onChange={(id, value) => setIsActive(value)}
                disabled={false}
              />
            </div>
          </div>

          <div className="buttons-container justify-end gap-12">
            <Button background="color2" size="medium" disabled={isDisabled} onClick={onSave}>
              Save
            </Button>
            <Button variant="danger" size="medium" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Thematic({canEdit}) {
  return (
      <AlertProvider>
        <ConfirmationProvider>
          <ThematicApiProvider>
            <ChannelApiProvider>
              <ThematicContent canEdit={canEdit}/>
            </ChannelApiProvider>
          </ThematicApiProvider>
        </ConfirmationProvider>
      </AlertProvider>
  );
}

export default Thematic;
